// src/pages/privacypolicy.tsx
import Nav from "../comps/nav";
import type { ReactNode } from "react";
import Footer from "../comps/footer";

type SectionProps = { title: string; children: ReactNode };

const Section = ({ title, children }: SectionProps) => (
    <section className="mt-10">
        <h2 className="text-xl font-semibold text-left">{title}</h2>
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="space-y-3 text-gray-800 text-left">{children}</div>
        </div>
    </section>
);

const PrivacyPolicy = () => {
    return (
        <main className="min-h-screen bg-[#f2f4f5] pb-20">
            <Nav />
            <section className="relative px-4 pt-12 pb-12">
                <div className="mx-auto max-w-3xl text-gray-800 text-left">
                    <h1 className="text-2xl font-semibold">Privacy Policy</h1>
                    <p className="text-sm text-gray-500 mt-1">Last updated: August 22, 2025</p>

                    <p className="mt-6">
                        This Privacy Policy explains how AMII handles your information. AMII provides an account-based
                        experience and stores certain data on our servers to power core features such as medication
                        tracking, document storage, and optional push notifications. We do not run in‑app advertising
                        and we do not sell your data.
                    </p>

                    <Section title="What this policy covers">
                        <p>
                            This policy applies to the AMII mobile application and the AMII backend/API that supports it.
                            It does not cover any third‑party sites you may visit from external links, which are governed
                            by their own policies.
                        </p>
                    </Section>

                    <Section title="Data we collect and process">
                        <p>
                            <span className="font-medium">Account and authentication data.</span> When you sign up, we
                            collect your name, email, and phone number. We store a password hash (not your plain password)
                            and verification status/code. We issue JSON Web Tokens (JWTs) for authentication. During email
                            verification or password reset flows, our backend may set a session cookie.
                        </p>
                        <p>
                            <span className="font-medium">Profile data.</span> You can optionally provide or edit details
                            such as address, date of birth, gender, blood type, medical conditions, allergies, and
                            emergency contact information. You can also upload a profile picture.
                        </p>
                        <p>
                            <span className="font-medium">Medication data.</span> Names, dosage, directions, frequency,
                            and scheduling/marked dates you add in the app are stored on our servers to enable retrieval
                            and features across sessions.
                        </p>
                        <p>
                            <span className="font-medium">Documents you upload.</span> Files you choose to upload (e.g.,
                            PDFs, images) are stored by us with associated metadata (e.g., filename, upload date).
                        </p>
                        <p>
                            <span className="font-medium">Push notifications (optional).</span> If you enable
                            notifications, we process a device push token and the notification payload to deliver reminders
                            via third‑party push services (Expo Notifications, Apple Push Notification service (APNs),
                            and/or Firebase Cloud Messaging (FCM)).
                        </p>
                        <p>
                            <span className="font-medium">Third‑party lookups you request.</span> When you search for drug
                            information by name or NDC, your query terms are sent to public FDA APIs to retrieve results.
                        </p>
                        <p>
                            <span className="font-medium">Support communications.</span> If you contact us for help, we
                            receive the information you provide so we can respond.
                        </p>
                        <p>
                            <span className="font-medium">Basic service logs.</span> Our servers may log standard request
                            information (e.g., IP address, user agent, timestamps) for security and operational purposes.
                            We do not use analytics/tracking SDKs in the app.
                        </p>
                    </Section>

                    <Section title="What we do not collect">
                        <p>No third‑party analytics or tracking SDKs in the app.</p>
                        <p>No precise location data.</p>
                        <p>No advertising identifiers and no third‑party ads.</p>
                    </Section>

                    <Section title="How we use your data">
                        <p>Authenticate your account, verify your email, and support password resets.</p>
                        <p>Provide core app functionality (medication tracking, profile, document storage, and pass generation).</p>
                        <p>Send push notifications you have opted into.</p>
                        <p>Send required emails (verification, password reset, and pass delivery).</p>
                        <p>Fetch drug information from FDA APIs based on your queries.</p>
                        <p>Maintain and secure our services, including preventing abuse and troubleshooting.</p>
                    </Section>

                    <Section title="Data storage and retention">
                        <p>
                            <span className="font-medium">On‑device.</span> We use on‑device storage (e.g., AsyncStorage)
                            for items like your auth token, username, and display preferences. You can clear these by
                            logging out or uninstalling the app.
                        </p>
                        <p>
                            <span className="font-medium">Server.</span> We store account, profile, medication, and
                            uploaded document data in our databases. Generated Apple Wallet passes may be stored in cloud
                            storage to enable delivery.
                        </p>
                        <p>
                            <span className="font-medium">Retention.</span> We retain personal data for as long as your
                            account is active or as needed to provide the service. You may delete uploaded documents
                            in‑app and request account deletion. We may retain minimal records where required by law or
                            for security.
                        </p>
                    </Section>

                    <Section title="Sharing with third parties">
                        <p>
                            <span className="font-medium">Push providers.</span> Expo Notifications, APNs (Apple), and/or
                            FCM (Google) receive your device’s push token and the notification payload solely to deliver
                            messages.
                        </p>
                        <p>
                            <span className="font-medium">Email delivery.</span> Our email provider processes recipient
                            addresses and message content to send verification, reset, and pass emails.
                        </p>
                        <p>
                            <span className="font-medium">Cloud storage.</span> We may use cloud storage (e.g., AWS S3) to
                            host generated pass files for delivery to you.
                        </p>
                        <p>
                            <span className="font-medium">Public FDA APIs.</span> Drug name or NDC query terms are sent to
                            FDA endpoints to return results you request.
                        </p>
                        <p>We do not sell or rent your personal data.</p>
                    </Section>

                    <Section title="Cookies and similar technologies">
                        <p>
                            The mobile app does not use cookies. During certain web‑based flows (such as email verification
                            or password reset), our backend may set a session cookie required to complete the process.
                        </p>
                    </Section>

                    <Section title="Your choices">
                        <p>Manage notifications in your device settings or within the app.</p>
                        <p>Edit profile and medication data in the app.</p>
                        <p>Upload and delete your documents in the app.</p>
                        <p>Log out to clear the on‑device auth token.</p>
                        <p>Contact us to request account and data deletion, or to request a copy of your data.</p>
                    </Section>

                    <Section title="Children’s privacy">
                        <p>
                            AMII is not directed to children under 13, and we do not knowingly collect personal information
                            from children under 13. If you believe a child has provided us with personal information, please
                            contact us so we can take appropriate action.
                        </p>
                    </Section>

                    <Section title="Changes to this policy">
                        <p>
                            We may update this policy from time to time. We will post the updated version in the app and
                            update the “Last updated” date above.
                        </p>
                    </Section>

                    <Section title="Contact us">
                        <p>Email: benson@amidex.co</p>
                        <p>
                            Support:{" "}
                            <a
                                href="https://www.amidex.co/amii/support"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                                className="text-blue-700 underline"
                            >
                                https://www.amidex.co/amii/support
                            </a>
                        </p>
                    </Section>
                </div>
            </section>
            <Footer/>
        </main>
    );
};

export default PrivacyPolicy;
